import React from 'react';

import { Flex } from '@lightspeed/flame/Core';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Text } from '@lightspeed/flame/Text';
import { useQuery } from '@tanstack/react-query';

import { createReactRenderer } from 'helpers/react-renderer';
import {
  CreditAccountSectionButton,
  CreditAccountUnavailableAlert,
  InTransitBadge,
  ResponsiveTable,
} from 'root/apps/QuoteMachineIntegration/components/CreditAccountBalance/styles';
import LoadingSkeletonCell from 'root/apps/QuoteMachineIntegration/components/LoadingSkeletonCell/LoadingSkeletonCell';
import { useBalanceDriftDetection } from 'root/apps/QuoteMachineIntegration/hooks/useBalanceDriftDetection';
import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import { formatMoney } from 'root/apps/QuoteMachineIntegration/utils/Money';
import TooltipHelp from 'root/apps/Tooltip/components/TooltipHelp';

import { useCheckInvoicingStatusOnSale } from './useCheckInvoicingStatusOnSale';

export interface CreditAccountBalanceProps {
  creditAccountID: string;
  creditAccountLimit: number | null;
  lsrBalanceOwed: number | null;
  lsrDepositBalance: number | null;
  triggerRefundDepositButton: boolean;
}

const CreditAccountBalance: React.FC<CreditAccountBalanceProps> = ({
  creditAccountID,
  creditAccountLimit,
  lsrBalanceOwed,
  lsrDepositBalance,
  triggerRefundDepositButton,
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['creditAccountBalance'],
    queryFn: () =>
      CustomerAccountService.getCustomerAccount(
        `${creditAccountID}`,
        `${creditAccountLimit ?? ''}`
      ),
    retry: false,
    cacheTime: 0,
    refetchOnMount: true,
  });

  const {
    triggerMutationObserver,
    saleHasDeposit,
    saleHasInvoicePayments,
    saleHasDepositRefunds,
    saleHasDepositPayments,
    saleHasOnAccountPayment,
    isRefundSale,
    areButtonsDisabled,
  } = useCheckInvoicingStatusOnSale();

  React.useEffect(() => {
    triggerMutationObserver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This will trigger the refund button click automatically if coming through flow: customer > sale (with deposit) > refund
  React.useEffect(() => {
    if (triggerRefundDepositButton) {
      const refundButton = document.getElementById('refundDepositButton') as HTMLButtonElement;
      if (refundButton) {
        refundButton.click();
      }
    }
  }, [areButtonsDisabled, triggerRefundDepositButton]);

  // Detect balance drift between LSR and Invoicing
  useBalanceDriftDetection({
    creditAccountID,
    invoicingData: data,
    lsrBalanceOwed,
    lsrDepositBalance,
  });

  if (isError) {
    return (
      <CreditAccountUnavailableAlert
        type="warning"
        noCloseBtn={true}
        icon={<IconWarning color="#F6B444" />}
      >
        Credit Account details are currently unavailable.
      </CreditAccountUnavailableAlert>
    );
  }

  const displayCreditAccountBalance =
    creditAccountLimit != null &&
    !(
      creditAccountLimit === 0 &&
      data?.availableAmountFromCreditLimit === 0 &&
      data?.owingBalance === 0
    );

  // owingBalance value should display with negative "-" sign, if not 0
  const owingBalance = !data?.owingBalance ? 0 : -data?.owingBalance;
  const depositBalance = !data?.depositBalance ? 0 : data?.depositBalance;
  const isMakePaymentAvailable =
    data?.owingBalance !== 0 && !saleHasDeposit && !saleHasDepositRefunds && !isRefundSale;
  const isRefundDepositAvailable =
    depositBalance > 0 &&
    !saleHasOnAccountPayment &&
    !saleHasDeposit &&
    !saleHasInvoicePayments &&
    !saleHasDepositPayments;
  const isAddDepositAvailable = !saleHasInvoicePayments && !saleHasDepositRefunds && !isRefundSale;
  const hasInTransit = (data?.totalProcessing ?? 0) > 0;

  const handleAddInvoicePayment = () => {
    window.merchantos.register.ensureAppIsDefined();
    window.App.channel.vent.trigger(window.App.globalEvents.register.ADD_INVOICE_PAYMENT);
  };

  const handleAddDeposit = () => {
    window.merchantos.register.ensureAppIsDefined();
    window.App.channel.vent.trigger(window.App.globalEvents.register.ADD_INVOICING_DEPOSIT);
  };

  return (
    <>
      <ResponsiveTable id="payments_customer_and_credit">
        <thead>
          <tr>
            <th colSpan={3} className="section" style={{ fontWeight: 600 }}>
              Credit Account
            </th>
          </tr>
        </thead>
        <tbody>
          {displayCreditAccountBalance && (
            <tr>
              <td>
                <LoadingSkeletonCell isLoading={isLoading} height={19}>
                  <Text>Outstanding invoices</Text>
                </LoadingSkeletonCell>
              </td>
              <td data-test="owingBalance">
                <LoadingSkeletonCell isLoading={isLoading} height={19}>
                  <Flex justifyContent="flex-end" style={{ gap: 8, alignItems: 'center' }}>
                    <span className="notranslate">
                      {data?.currency && formatMoney(owingBalance, data.currency)}
                    </span>
                    {hasInTransit && data?.currency && (
                      <InTransitBadge data-test="inTransitBadge">
                        <span className="notranslate">
                          {formatMoney(data.totalProcessing!, data.currency)}
                        </span>
                        <span>in transit</span>
                      </InTransitBadge>
                    )}
                  </Flex>
                </LoadingSkeletonCell>
              </td>
              <td>
                <LoadingSkeletonCell isLoading={isLoading} height={19}>
                  {isMakePaymentAvailable &&
                    (saleHasOnAccountPayment ? (
                      <Flex justifyContent="flex-end">
                        <TooltipHelp
                          content="Remove the Account payment to make a payment."
                          placement="bottom"
                          icon={
                            <CreditAccountSectionButton disabled={true}>
                              {!saleHasInvoicePayments ? 'Make payment' : 'Edit payment'}
                            </CreditAccountSectionButton>
                          }
                          width="216px"
                        />
                      </Flex>
                    ) : (
                      <Flex justifyContent="flex-end">
                        <CreditAccountSectionButton
                          disabled={areButtonsDisabled}
                          onClick={handleAddInvoicePayment}
                        >
                          {!saleHasInvoicePayments ? 'Make payment' : 'Edit payment'}
                        </CreditAccountSectionButton>
                      </Flex>
                    ))}
                </LoadingSkeletonCell>
              </td>
            </tr>
          )}
          <tr>
            <td>
              <LoadingSkeletonCell isLoading={isLoading} height={19}>
                <Text>Deposit balance</Text>
              </LoadingSkeletonCell>
            </td>
            <td data-test="depositBalance" className="notranslate">
              <LoadingSkeletonCell isLoading={isLoading} height={19}>
                <Flex justifyContent="flex-end">
                  <span className="notranslate">
                    {data?.currency && formatMoney(depositBalance, data.currency)}
                  </span>
                </Flex>
              </LoadingSkeletonCell>
            </td>
            <td>
              <LoadingSkeletonCell isLoading={isLoading} height={19}>
                <Flex justifyContent="flex-end" style={{ gap: 8 }}>
                  {isRefundDepositAvailable && (
                    <CreditAccountSectionButton
                      id="refundDepositButton"
                      disabled={areButtonsDisabled}
                      onClick={() => window.merchantos.register.account_actions.withdraw()}
                      data-trigger-on-enabled={`${triggerRefundDepositButton}`}
                    >
                      Refund deposit
                    </CreditAccountSectionButton>
                  )}
                  {isAddDepositAvailable &&
                    (saleHasOnAccountPayment ? (
                      <TooltipHelp
                        content="Remove the Account payment to add a deposit."
                        placement="bottom"
                        icon={
                          <CreditAccountSectionButton disabled={true}>
                            {!saleHasDeposit ? 'Add deposit' : 'Edit deposit'}
                          </CreditAccountSectionButton>
                        }
                        width="216px"
                      />
                    ) : (
                      <CreditAccountSectionButton
                        disabled={areButtonsDisabled}
                        onClick={handleAddDeposit}
                      >
                        {!saleHasDeposit ? 'Add deposit' : 'Edit deposit'}
                      </CreditAccountSectionButton>
                    ))}
                </Flex>
              </LoadingSkeletonCell>
            </td>
          </tr>
        </tbody>
      </ResponsiveTable>
      {/* Below div displays the content of the refund deposit action */}
      <div id="account_actions" style={{ paddingLeft: '15px', paddingRight: '15px' }} />
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: CreditAccountBalance,
  selector: '[data-react-root="CreditAccountBalance"]',
});

history.onChangeState(
  { pathname: '/register.php', form_name: undefined, id: undefined, view: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default CreditAccountBalance;
